// Bonus mode dispatch (docs/04 §17). Payouts are in par = T(n) / S(n). Hidden Plug parts live in
// the bonuses once the good path opens (dealsRefused ≥ GOOD_PATH_DEALS).
import { B } from '../../content/balance';
import { D, type Decimal } from '../decimal';
import { say } from '../dialogue';
import { giveCharm, startNightShift } from '../grants';
import { setHunger } from '../hunger';
import { addShard } from '../meta';
import type { Modifiers } from '../modifiers';
import { stream } from '../rng';
import { par } from '../tribute';
import type { BonusInput, BonusMode, GameEvent, GameState, Rarity } from '../types';
import { afterSpin } from '../window';
import { createHilo, hiloAuto, hiloCollect, hiloGuess } from './hilo';
import { createScratch, scratchAuto, scratchCashOut, scratchPlugFound, scratchReveal } from './scratch';
import { createWheel, wheelStop } from './wheel';
import { createPlinko, plinkoAuto, plinkoDrop } from './plinko';
import { createFeast, feastAuto, feastSpin } from './feast';
import { dims } from '../grid';

/** Plug part `id` can still turn up (good path, not found yet). */
export function plugLive(state: GameState, id: string): boolean {
  const l = state.meta.lifetime;
  return l.dealsRefused >= B.GOOD_PATH_DEALS && !l.plugParts.includes(id);
}

function findPlug(state: GameState, id: string, events: GameEvent[]): void {
  const l = state.meta.lifetime;
  if (l.plugParts.includes(id)) return;
  l.plugParts.push(id);
  events.push({ e: 'PLUG_FOUND', id });
}

export function startBonus(
  state: GameState,
  mods: Modifiers,
  mode: BonusMode,
  events: GameEvent[],
  opts: { stake?: Decimal } = {},
): void {
  const run = state.run!;
  // The Maw tires of its games: each bonus already played this run pays a little less.
  const p = par(run.window.tribute, run.window.spinsTotal).mul(Math.pow(B.BONUS_FATIGUE, run.stats.bonuses));
  const rng = stream(run.rng, 'bonus');
  if (mode === 'scratch') {
    const plug = plugLive(state, 'plug_1') && rng.chance(B.SCRATCH_PLUG_CHANCE);
    run.bonus = createScratch(rng, p, { size: mods.scratchSize, noTrap: mods.scratchNoTrap, plug });
  } else if (mode === 'hilo') {
    run.bonus = opts.stake ? createHilo(rng, p, opts.stake, 'gamble') : createHilo(rng, p, p.mul(B.HILO_SIGIL_BASE), 'sigil');
  } else if (mode === 'wheel') run.bonus = createWheel(p);
  else if (mode === 'plinko') run.bonus = createPlinko(rng, p, mods, plugLive(state, 'plug_4') && rng.chance(B.PLINKO_PLUG_CHANCE));
  else if (mode === 'feast' && dims(run.size).reels >= 5 && dims(run.size).rows >= 3) {
    const { reels, rows } = dims(run.size);
    run.bonus = createFeast(rng, p, reels, rows, mods);
  } else {
    afterSpin(state, mods, events);
    return;
  }
  run.phase = 'bonus';
  run.stats.bonuses++;
  run.window.counters[`bonus_${mode}`] = (run.window.counters[`bonus_${mode}`] ?? 0) + 1;
  state.meta.lifetime.bonuses[mode] = (state.meta.lifetime.bonuses[mode] ?? 0) + 1;
  events.push({ e: 'BONUS_TRIGGER', mode });
  events.push({ e: 'BONUS_UPDATE', bonus: run.bonus });
  say(state, mode === 'scratch' ? 'bonus' : mode, events);
}

export function bonusInput(state: GameState, mods: Modifiers, input: BonusInput, events: GameEvent[]): string | undefined {
  const run = state.run;
  const b = run?.bonus;
  if (!run || !b || run.phase !== 'bonus') return 'no bonus';
  const rng = stream(run.rng, 'bonus');
  if (b.mode === 'scratch') {
    if (input.kind === 'scratch') scratchReveal(b, input.cell, input.revealed);
    else if (input.kind === 'scratch_cashout') scratchCashOut(b);
    else if (input.kind === 'auto') scratchAuto(b);
    else return 'wrong input';
  } else if (b.mode === 'hilo') {
    if (input.kind === 'hilo') {
      const r = hiloGuess(b, input.guess, mods, rng);
      if (r === 'up') say(state, 'hiloUp', events);
      if (r === 'top' && plugLive(state, 'plug_2')) b.plug = true;
    } else if (input.kind === 'hilo_collect') hiloCollect(b);
    else if (input.kind === 'auto') hiloAuto(b, mods, rng);
    else return 'wrong input';
  } else if (b.mode === 'plinko') {
    if (input.kind === 'plinko_drop') plinkoDrop(b, input.slot, rng);
    else if (input.kind === 'auto') plinkoAuto(b, rng);
    else return 'wrong input';
  } else if (b.mode === 'feast') {
    const live = plugLive(state, 'plug_5');
    if (input.kind === 'feast_spin') feastSpin(b, rng, live);
    else if (input.kind === 'auto') feastAuto(b, rng, live);
    else return 'wrong input';
  } else if (b.mode === 'wheel') {
    if (input.kind !== 'wheel_stop' && input.kind !== 'auto') return 'wrong input';
    const live = plugLive(state, 'plug_3');
    const stopOnce = (hit: boolean) => {
      const seg = wheelStop(b, hit, mods, rng, live);
      if (seg === 'step') say(state, 'stepIn', events, undefined, { force: true });
    };
    if (input.kind === 'wheel_stop') stopOnce(input.hit);
    else while (!b.done) stopOnce(rng.chance(Math.min(0.9, B.WHEEL_AUTO_HIT * mods.wheelBand)));
  }
  events.push({ e: 'BONUS_UPDATE', bonus: b });
  if (b.done) finishBonus(state, mods, events);
  return undefined;
}

function finishBonus(state: GameState, mods: Modifiers, events: GameEvent[]): void {
  const run = state.run!;
  const b = run.bonus!;
  const raw = b.payout.mul(mods.bonusPayoutX).floor();
  // Guard: a bonus can never pay NaN or a negative amount.
  const payout = Number.isFinite(raw.mag) && raw.gte(0) ? raw : D(0);
  run.coins = run.coins.add(payout);
  if (payout.gt(run.stats.bestWin)) run.stats.bestWin = payout;
  const rng = stream(run.rng, 'bonus');
  if (b.mode === 'scratch' && scratchPlugFound(b)) findPlug(state, 'plug_1', events);
  if (b.mode === 'hilo') {
    if (b.plug) findPlug(state, 'plug_2', events);
    if (b.devoured) {
      setHunger(state, mods, run.hunger - B.HILO_DEVOUR_HUNGER, events);
      say(state, 'hiloDevoured', events, undefined, { force: true });
    } else say(state, 'hiloCollect', events);
  }
  if (b.mode === 'plinko') {
    if (b.drops.some((d) => d.prize === 'plug')) findPlug(state, 'plug_4', events);
    if (b.chomps > 0) {
      setHunger(state, mods, run.hunger - B.PLINKO_CHOMP_HUNGER * b.chomps, events);
      say(state, 'plinkoChomp', events);
    }
    if (b.drops.filter((d) => d.bucket === Math.floor(B.PLINKO_BUCKETS.length / 2)).length >= 3) state.meta.flags.plinkoPrince = true;
  }
  if (b.mode === 'feast') {
    if (b.cells.some((c) => c?.kind === 'plug')) findPlug(state, 'plug_5', events);
    if (b.full) {
      state.meta.flags.theFeast = true;
      say(state, 'feastFull', events, undefined, { force: true });
    }
  }
  if (b.mode === 'wheel') {
    const last = b.prizes[b.prizes.length - 1] ?? '';
    if (b.night > 0) startNightShift(state, mods, b.night, events);
    else if (last === 'charm' || last === 'legendary') {
      const rarity: Rarity = last === 'legendary' ? 'L' : rng.chance(0.5) ? 'U' : 'R';
      if (run.charms.length < mods.charmSlots) giveCharm(state, mods, rarity, rng, 'wheel', events);
      else run.coins = run.coins.add(b.par.mul(rarity === 'L' ? 150 : 40).floor());
    } else if (last === 'shard') {
      if (state.meta.molts > 0) addShard(state, (ids) => rng.pick(ids), events);
      else run.coins = run.coins.add(b.par.mul(100).floor());
    } else if (last === 'plug') findPlug(state, 'plug_3', events);
  }
  events.push({ e: 'BONUS_END', mode: b.mode, payout: payout.toString(), voided: b.voided });
  if (b.mode === 'scratch' && b.voided) say(state, 'bonusVoid', events);
  run.bonus = null;
  afterSpin(state, mods, events);
}
